// src/belediye/pages/dashboard/components/DashboardExpandPanel.jsx
import React, { useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import {
  XMarkIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  InboxIcon,
  ArrowsPointingOutIcon,
  CalendarDaysIcon,
  BuildingOffice2Icon,
  ClockIcon,
  MapPinIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";
import "./DashboardExpandPanel.css";

/* ============================================================================
   Helpers
============================================================================ */

const sayiFormat = (v) => {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("tr-TR").format(n);
};

const yuzdeFormat = (v) => {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `%${new Intl.NumberFormat("tr-TR", {
    maximumFractionDigits: 2,
  }).format(n)}`;
};

const saatFormat = (v) => {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${new Intl.NumberFormat("tr-TR", {
    maximumFractionDigits: 2,
  }).format(n)} saat`;
};

const trTarihSaat = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
};

const durumEtiketi = (durum) => {
  const map = {
    yeni: "Yeni",
    işleme_alındı: "İncelemede",
    çözüldü: "Çözüldü",
    çözülemedi: "Çözülemedi",
  };
  return map[String(durum || "")] || String(durum || "—");
};

const beklemeEtiketi = (row) => {
  if (row?.beklemeEtiketi) return row.beklemeEtiketi;

  const saat = Number(row?.acikSaat);
  if (!Number.isFinite(saat)) return "—";
  if (saat < 24) return `${saat}s`;

  const gun = Math.floor(saat / 24);
  const kalanSaat = saat % 24;
  return kalanSaat > 0 ? `${gun}g ${kalanSaat}s` : `${gun}g`;
};

const classNames = (...names) => names.filter(Boolean).join(" ");

const durumBadgeClass = (durum) =>
  classNames(
    "dep-status-badge",
    durum === "yeni" && "dep-status-badge--new",
    durum === "işleme_alındı" && "dep-status-badge--review",
    durum === "çözüldü" && "dep-status-badge--solved",
    durum === "çözülemedi" && "dep-status-badge--unsolved"
  );

const efektifBaslikMap = {
  latest_new: "Yeni Başvurular",
  waiting_new: "Bekleyen Başvurular",
  neighborhood_breakdown: "Mahalle Kırılımı",
  unit_breakdown: "Birim Kırılımı",
  open_age_distribution: "Açık Başvuru Süre Dağılımı",
};

const filtreOzetMetni = (durumlar) => {
  if (!Array.isArray(durumlar) || durumlar.length === 0) return null;
  if (durumlar.includes("tümü")) return "Durum: Tümü";

  const etiketler = durumlar.map((d) => durumEtiketi(d));
  if (etiketler.length <= 2) return `Durum: ${etiketler.join(", ")}`;
  return `Durum: ${etiketler.length} seçim`;
};

/* ============================================================================
   Small UI pieces
============================================================================ */

function PanelMetaPill({ icon: Icon, children }) {
  return (
    <span className="dep-meta-pill">
      {Icon ? <Icon className="dep-meta-pill-icon" /> : null}
      <span>{children}</span>
    </span>
  );
}

function LoadingState({ title = "Yükleniyor..." }) {
  return (
    <div className="dep-state dep-state--loading">
      <ArrowPathIcon className="dep-spin" />
      <div className="dep-state-title">{title}</div>
      <div className="dep-state-sub">Detay veri alınıyor...</div>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="dep-state dep-state--error">
      <ExclamationTriangleIcon />
      <div className="dep-state-title">Detay alınamadı</div>
      <div className="dep-state-sub">
        {message || "Bilinmeyen bir hata oluştu."}
      </div>

      {typeof onRetry === "function" && (
        <button type="button" className="dep-retry-btn" onClick={onRetry}>
          <ArrowPathIcon />
          <span>Tekrar dene</span>
        </button>
      )}
    </div>
  );
}

function EmptyState({
  title = "Veri yok",
  subtitle = "Gösterilecek içerik bulunamadı.",
}) {
  return (
    <div className="dep-state dep-state--empty">
      <InboxIcon />
      <div className="dep-state-title">{title}</div>
      <div className="dep-state-sub">{subtitle}</div>
    </div>
  );
}

function BarList({ rows = [], labelKey = "etiket", valueKey = "sayi", percentKey }) {
  if (!rows.length) {
    return <EmptyState />;
  }

  const max = Math.max(1, ...rows.map((r) => Number(r?.[valueKey] || 0)));

  return (
    <div className="dep-bars">
      {rows.map((row, idx) => {
        const value = Number(row?.[valueKey] || 0);
        const width = (value / max) * 100;
        const label = row?.[labelKey] || "—";

        const percentText =
          percentKey &&
          row?.[percentKey] !== undefined &&
          row?.[percentKey] !== null
            ? yuzdeFormat(row[percentKey])
            : null;

        return (
          <div className="dep-bar-row" key={`${label}-${idx}`}>
            <div className="dep-bar-row-top">
              <div className="dep-bar-label" title={label}>
                {label}
              </div>

              <div className="dep-bar-value-wrap">
                {percentText ? (
                  <span className="dep-bar-secondary">{percentText}</span>
                ) : null}
                <span className="dep-bar-value">{sayiFormat(value)}</span>
              </div>
            </div>

            <div className="dep-bar-track">
              <div className="dep-bar-fill" style={{ width: `${width}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ApplicationListTable({ rows = [], waitingMode = false }) {
  if (!rows.length) {
    return <EmptyState />;
  }

  return (
    <div className="dep-table-wrap">
      <table className="dep-table">
        <thead>
          <tr>
            <th>Takip No</th>
            <th>Başlık</th>
            <th>Birim</th>
            <th>Mahalle</th>
            <th>Durum</th>
            <th>Tarih</th>
            {waitingMode ? <th>Bekleme</th> : null}
          </tr>
        </thead>

        <tbody>
          {rows.map((row, idx) => (
            <tr key={row?.id || `${row?.takipNo || "row"}-${idx}`}>
              <td className="dep-cell-mono">{row?.takipNo || "—"}</td>
              <td className="dep-cell-title">{row?.baslik || "—"}</td>
              <td>{row?.birimAdi || "—"}</td>
              <td>{row?.mahalleAdi || "—"}</td>
              <td>
                <span className={durumBadgeClass(row?.durum)}>
                  {durumEtiketi(row?.durum)}
                </span>
              </td>
              <td>{trTarihSaat(row?.tarih)}</td>
              {waitingMode ? (
                <td className="dep-cell-wait">{beklemeEtiketi(row)}</td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BreakdownTable({ rows = [], type = "neighborhood" }) {
  if (!rows.length) {
    return <EmptyState />;
  }

  const labelKey = type === "unit" ? "birimAdi" : "mahalleAdi";
  const idKey = type === "unit" ? "birimId" : "mahalleId";

  return (
    <div className="dep-table-wrap">
      <table className="dep-table">
        <thead>
          <tr>
            <th>{type === "unit" ? "Birim" : "Mahalle"}</th>
            <th>Toplam</th>
            <th>Açık</th>
            <th>Kapalı</th>
            <th>Çözüm Oranı</th>
            {type === "unit" ? <th>Ort. Çözüm</th> : null}
            <th>Pay</th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row, idx) => (
            <tr key={row?.[idKey] ?? `${labelKey}-${idx}`}>
              <td className="dep-cell-title">{row?.[labelKey] || "—"}</td>
              <td>{sayiFormat(row?.toplam)}</td>
              <td>{sayiFormat(row?.acik)}</td>
              <td>{sayiFormat(row?.kapali)}</td>
              <td>{yuzdeFormat(row?.cozumOraniYuzde)}</td>
              {type === "unit" ? (
                <td>{saatFormat(row?.ortalamaCozumSaati)}</td>
              ) : null}
              <td>{yuzdeFormat(row?.payOraniYuzde)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ============================================================================
   Widget renderer
============================================================================ */

function renderSummaryFallback(widgetKey, summaryData) {
  if (!summaryData) return null;

  if (widgetKey === "latest_new") {
    return <ApplicationListTable rows={summaryData} waitingMode={false} />;
  }

  if (widgetKey === "waiting_new") {
    return <ApplicationListTable rows={summaryData} waitingMode />;
  }

  if (widgetKey === "neighborhood_breakdown") {
    return <BreakdownTable rows={summaryData} type="neighborhood" />;
  }

  if (widgetKey === "unit_breakdown") {
    return <BreakdownTable rows={summaryData} type="unit" />;
  }

  if (widgetKey === "open_age_distribution") {
    return <BarList rows={summaryData} labelKey="aralik" valueKey="sayi" />;
  }

  return null;
}

function renderDetailWidget(widgetKey, widgetData, summaryData) {
  if (!widgetData && summaryData) {
    return renderSummaryFallback(widgetKey, summaryData);
  }

  if (!widgetData) {
    return <EmptyState />;
  }

  if (widgetKey === "latest_new") {
    return (
      <ApplicationListTable
        rows={widgetData?.kayitlar || []}
        waitingMode={false}
      />
    );
  }

  if (widgetKey === "waiting_new") {
    return <ApplicationListTable rows={widgetData?.kayitlar || []} waitingMode />;
  }

  if (widgetKey === "neighborhood_breakdown") {
    return (
      <BreakdownTable
        rows={widgetData?.kayitlar || []}
        type="neighborhood"
      />
    );
  }

  if (widgetKey === "unit_breakdown") {
    return <BreakdownTable rows={widgetData?.kayitlar || []} type="unit" />;
  }

  if (widgetKey === "open_age_distribution") {
    return (
      <BarList
        rows={widgetData?.kayitlar || []}
        labelKey="aralik"
        valueKey="sayi"
      />
    );
  }

  return <EmptyState title="Bilinmeyen widget" subtitle={widgetKey} />;
}

/* ============================================================================
   Main component
============================================================================ */

export default function DashboardExpandPanel({
  open,
  title,
  subtitle,
  widgetKey,
  summaryData,
  detailEnvelope,
  loading,
  error,
  onClose,
  onRetry,
  onPrint,
}) {
  const panelRef = useRef(null);
  const previouslyFocusedElementRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    previouslyFocusedElementRef.current = document.activeElement;

    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusTimer = window.setTimeout(() => {
      panelRef.current?.focus();
    }, 0);

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousBodyOverflow;

      if (
        previouslyFocusedElementRef.current &&
        typeof previouslyFocusedElementRef.current.focus === "function"
      ) {
        previouslyFocusedElementRef.current.focus();
      }
    };
  }, [open, onClose]);

  const applied = detailEnvelope?.uygulananFiltreler || null;
  const detailMeta = detailEnvelope?.meta || null;
  const widgetData = detailEnvelope?.widgetData || null;

  const totalCount = useMemo(() => {
    if (widgetData?.toplam !== undefined && widgetData?.toplam !== null) {
      return widgetData.toplam;
    }
    if (Array.isArray(widgetData?.kayitlar)) {
      return widgetData.kayitlar.length;
    }
    if (Array.isArray(summaryData)) {
      return summaryData.length;
    }
    return null;
  }, [widgetData, summaryData]);

  const effectiveTitle =
    title || efektifBaslikMap[widgetKey] || "Detay";

  const defaultSubtitle =
    subtitle ||
    applied?.seciliAralikBasligi ||
    detailMeta?.seciliAralikBasligi ||
    null;

  const durumOzet = filtreOzetMetni(
    applied?.durumlar || detailMeta?.durumlar || null
  );

  const titleId = `dashboard-expand-title-${widgetKey || "default"}`;

  if (!open) return null;

  return (
    <div
      className="dep-root"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-label={!effectiveTitle ? "Detay paneli" : undefined}
    >
      <div className="dep-backdrop" onClick={onClose} />

      <section
        ref={panelRef}
        className="dep-panel"
        tabIndex={-1}
      >
        <header className="dep-header">
          <div className="dep-header-left">
            <div className="dep-title-row">
              <ArrowsPointingOutIcon className="dep-title-icon" />
              <h3 id={titleId} className="dep-title">
                {effectiveTitle}
              </h3>
            </div>

            <div className="dep-meta-row">
              {defaultSubtitle ? (
                <PanelMetaPill icon={CalendarDaysIcon}>
                  {defaultSubtitle}
                </PanelMetaPill>
              ) : null}

              {detailMeta?.birimModu ? (
                <PanelMetaPill icon={BuildingOffice2Icon}>
                  {detailMeta.birimModu === "tümü"
                    ? "Tüm birimler"
                    : detailMeta.birimModu === "seçili"
                      ? "Seçili birim"
                      : "Aktif birim"}
                </PanelMetaPill>
              ) : null}

              {durumOzet ? <PanelMetaPill>{durumOzet}</PanelMetaPill> : null}

              {widgetKey === "waiting_new" ? (
                <PanelMetaPill icon={ClockIcon}>
                  Bekleme odaklı görünüm
                </PanelMetaPill>
              ) : null}

              {widgetKey === "neighborhood_breakdown" ? (
                <PanelMetaPill icon={MapPinIcon}>
                  Tüm görünür mahalleler
                </PanelMetaPill>
              ) : null}

              {totalCount !== null ? (
                <PanelMetaPill>{`${sayiFormat(totalCount)} kayıt`}</PanelMetaPill>
              ) : null}
            </div>
          </div>

          <div className="dep-header-actions">
            {typeof onRetry === "function" ? (
              <button
                type="button"
                className="dep-icon-btn"
                onClick={onRetry}
                title="Yenile"
                aria-label="Yenile"
              >
                <ArrowPathIcon />
              </button>
            ) : null}

            {typeof onPrint === "function" ? (
              <button
                type="button"
                className="dep-icon-btn"
                onClick={onPrint}
                title="Yazdır"
                aria-label="Yazdır"
              >
                <PrinterIcon />
              </button>
            ) : null}

            <button
              type="button"
              className="dep-icon-btn dep-icon-btn--close"
              onClick={onClose}
              title="Kapat"
              aria-label="Kapat"
            >
              <XMarkIcon />
            </button>
          </div>
        </header>

        <div className="dep-body">
          {loading ? (
            <LoadingState title="Detay yükleniyor..." />
          ) : error ? (
            <ErrorState message={error?.mesaj} onRetry={onRetry} />
          ) : (
            renderDetailWidget(widgetKey, widgetData, summaryData)
          )}
        </div>
      </section>
    </div>
  );
}

DashboardExpandPanel.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  widgetKey: PropTypes.string,
  summaryData: PropTypes.any,
  detailEnvelope: PropTypes.shape({
    meta: PropTypes.object,
    uygulananFiltreler: PropTypes.object,
    widgetData: PropTypes.object,
    uygulananParametreler: PropTypes.object,
    loadedAt: PropTypes.number,
    requestId: PropTypes.string,
  }),
  loading: PropTypes.bool,
  error: PropTypes.object,
  onClose: PropTypes.func,
  onRetry: PropTypes.func,
  onPrint: PropTypes.func,
};

DashboardExpandPanel.defaultProps = {
  open: false,
  title: "",
  subtitle: "",
  widgetKey: "",
  summaryData: null,
  detailEnvelope: null,
  loading: false,
  error: null,
  onClose: undefined,
  onRetry: undefined,
  onPrint: undefined,
};